/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-10-06
 */

import {
  ColorPalette,
  ColorPickerValue,
  ContrastRatios,
  AccessibilityWarning,
  AccessibilityValidation,
  OnColors,
  BaseThemeColors,
} from '../types/theme';

/**
 * Convert hex color to RGB values
 */
export function hexToRgb(
  hex: string
): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Convert RGB to hex
 */
export function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number): string => {
    const hex = n.toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Convert RGB to HSL
 */
export function rgbToHsl(
  rParam: number,
  gParam: number,
  bParam: number
): { h: number; s: number; l: number } {
  const r = rParam / 255;
  const g = gParam / 255;
  const b = bParam / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h: number;
  let s: number;
  const l = (max + min) / 2;

  if (max === min) {
    h = 0; // achromatic
    s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
      default:
        h = 0;
    }
    h /= 6;
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
}

/**
 * Convert HSL to RGB
 */
export function hslToRgb(
  hParam: number,
  sParam: number,
  lParam: number
): { r: number; g: number; b: number } {
  const h = hParam / 360;
  const s = sParam / 100;
  const l = lParam / 100;

  const hue2rgb = (p: number, q: number, tParam: number): number => {
    let t = tParam;
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  let r: number;
  let g: number;
  let b: number;

  if (s === 0) {
    r = l; // achromatic
    g = l; // achromatic
    b = l; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
}

/**
 * Calculate relative luminance for WCAG contrast calculations
 */
export function getRelativeLuminance(r: number, g: number, b: number): number {
  const getRsRGB = (colorParam: number): number => {
    const color = colorParam / 255;
    return color <= 0.03928 ? color / 12.92 : ((color + 0.055) / 1.055) ** 2.4;
  };

  return 0.2126 * getRsRGB(r) + 0.7152 * getRsRGB(g) + 0.0722 * getRsRGB(b);
}

/**
 * Calculate contrast ratio between two colors
 */
export function getContrastRatio(color1: string, color2: string): number {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);

  if (!rgb1 || !rgb2) return 1;

  const l1 = getRelativeLuminance(rgb1.r, rgb1.g, rgb1.b);
  const l2 = getRelativeLuminance(rgb2.r, rgb2.g, rgb2.b);

  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);

  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Generate 50-950 color palette from base color
 */
export function generateColorPalette(baseColor: string): ColorPalette {
  const rgb = hexToRgb(baseColor);
  if (!rgb) throw new Error(`Invalid color: ${baseColor}`);

  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

  // Generate lighter shades (50-400)
  const lightShades = [
    { key: '50', lightness: 95 },
    { key: '100', lightness: 90 },
    { key: '200', lightness: 80 },
    { key: '300', lightness: 70 },
    { key: '400', lightness: 60 },
  ];

  // Generate darker shades (600-950)
  const darkShades = [
    { key: '600', lightness: 40 },
    { key: '700', lightness: 30 },
    { key: '800', lightness: 25 },
    { key: '900', lightness: 20 },
    { key: '950', lightness: 10 },
  ];

  const palette: Partial<ColorPalette> = {
    500: baseColor, // Base color
  };

  // Generate light shades
  lightShades.forEach(({ key, lightness }) => {
    const newHsl = { ...hsl, l: lightness };
    const newRgb = hslToRgb(newHsl.h, newHsl.s, newHsl.l);
    (palette as any)[key] = rgbToHex(newRgb.r, newRgb.g, newRgb.b);
  });

  // Generate dark shades
  darkShades.forEach(({ key, lightness }) => {
    const newHsl = { ...hsl, l: lightness };
    const newRgb = hslToRgb(newHsl.h, newHsl.s, newHsl.l);
    (palette as any)[key] = rgbToHex(newRgb.r, newRgb.g, newRgb.b);
  });

  return palette as ColorPalette;
}

/**
 * Calculate appropriate "on" color (text) for a given background color
 */
export function calculateOnColor(backgroundColor: string): string {
  const whiteContrast = getContrastRatio('#FFFFFF', backgroundColor);
  const blackContrast = getContrastRatio('#000000', backgroundColor);

  // Return color with better contrast ratio
  return whiteContrast > blackContrast ? '#FFFFFF' : '#000000';
}

/**
 * Generate all "on" colors for the theme
 */
export function generateOnColors(colors: BaseThemeColors): OnColors {
  return {
    onPrimary: calculateOnColor(colors.primary),
    onSecondary: calculateOnColor(colors.secondary),
    onNeutral: calculateOnColor(colors.neutral),
    onSuccess: calculateOnColor(colors.success),
    onWarning: calculateOnColor(colors.warning),
    onError: calculateOnColor(colors.error),
    onBackground: calculateOnColor(colors.background),
    onSurface: calculateOnColor(colors.surface),
  };
}

/**
 * Calculate contrast ratios for all important color combinations
 */
export function calculateContrastRatios(
  colors: BaseThemeColors,
  onColors: OnColors
): ContrastRatios {
  return {
    textOnBackground: getContrastRatio(colors.text.primary, colors.background),
    textOnSurface: getContrastRatio(colors.text.primary, colors.surface),
    onPrimaryOnPrimary: getContrastRatio(onColors.onPrimary, colors.primary),
    onSecondaryOnSecondary: getContrastRatio(
      onColors.onSecondary,
      colors.secondary
    ),
    onSuccessOnSuccess: getContrastRatio(onColors.onSuccess, colors.success),
    onWarningOnWarning: getContrastRatio(onColors.onWarning, colors.warning),
    onErrorOnError: getContrastRatio(onColors.onError, colors.error),
    textSecondaryOnBackground: getContrastRatio(
      colors.text.secondary,
      colors.background
    ),
    textSecondaryOnSurface: getContrastRatio(
      colors.text.secondary,
      colors.surface
    ),
    textInverseOnPrimary: getContrastRatio(colors.text.inverse, colors.primary),
  };
}

/**
 * Generate detailed accessibility recommendation
 */
function generateRecommendation(ratio: number, description: string): string {
  if (ratio < 3) {
    return `Critical: ${description} fails WCAG standards. Change colors immediately.`;
  }
  if (ratio < 4.5) {
    return `Warning: ${description} doesn't meet WCAG AA. Consider darker/lighter alternatives.`;
  }
  if (ratio < 7) {
    return `Good: ${description} meets WCAG AA. For AAA compliance, increase contrast further.`;
  }
  return `Excellent: ${description} exceeds WCAG AAA standards.`;
}

/**
 * Calculate weighted accessibility score
 */
function calculateAccessibilityScore(
  contrastRatios: ContrastRatios,
  warnings: AccessibilityWarning[]
): number {
  // Weight different contrast ratios by importance
  const weights = {
    textOnBackground: 30, // Most important - main content
    textOnSurface: 25, // Important - cards and components
    onPrimaryOnPrimary: 15, // Important - primary actions
    onSecondaryOnSecondary: 10, // Moderate - secondary actions
    onErrorOnError: 10, // Important - error states
    onSuccessOnSuccess: 5, // Moderate - success states
    onWarningOnWarning: 5, // Moderate - warning states
  };

  let totalScore = 0;
  let totalWeight = 0;

  Object.entries(contrastRatios).forEach(([key, ratio]) => {
    const weight = weights[key as keyof typeof weights] || 5;

    // Score based on WCAG compliance
    let score = 0;
    if (ratio >= 7)
      score = 100; // AAA
    else if (ratio >= 4.5)
      score = 85; // AA
    else if (ratio >= 3)
      score = 60; // Large text AA
    else if (ratio >= 2)
      score = 30; // Poor
    else score = 0; // Critical

    totalScore += score * weight;
    totalWeight += weight;
  });

  // Apply penalty for warnings
  const errorPenalty = warnings.filter(w => w.severity === 'error').length * 10;
  const warningPenalty =
    warnings.filter(w => w.severity === 'warning').length * 5;

  const baseScore = totalWeight > 0 ? totalScore / totalWeight : 0;
  const finalScore = Math.max(0, baseScore - errorPenalty - warningPenalty);

  return Math.round(finalScore);
}

/**
 * Validate accessibility and generate comprehensive warnings
 */
export function validateAccessibility(
  colors: BaseThemeColors,
  onColors: OnColors
): AccessibilityValidation {
  const contrastRatios = calculateContrastRatios(colors, onColors);
  const warnings: AccessibilityWarning[] = [];

  // Comprehensive contrast checks with detailed analysis
  const checks = [
    {
      id: 'text-on-background',
      ratio: contrastRatios.textOnBackground,
      foreground: colors.text.primary,
      background: colors.background,
      description: 'Primary text on page background',
      importance: 'critical',
    },
    {
      id: 'text-on-surface',
      ratio: contrastRatios.textOnSurface,
      foreground: colors.text.primary,
      background: colors.surface,
      description: 'Primary text on card/component surfaces',
      importance: 'critical',
    },
    {
      id: 'text-secondary-on-background',
      ratio: contrastRatios.textSecondaryOnBackground,
      foreground: colors.text.secondary,
      background: colors.background,
      description: 'Secondary text on page background',
      importance: 'high',
    },
    {
      id: 'text-secondary-on-surface',
      ratio: contrastRatios.textSecondaryOnSurface,
      foreground: colors.text.secondary,
      background: colors.surface,
      description: 'Secondary text on card/component surfaces',
      importance: 'high',
    },
    {
      id: 'on-primary-on-primary',
      ratio: contrastRatios.onPrimaryOnPrimary,
      foreground: onColors.onPrimary,
      background: colors.primary,
      description: 'Text on primary buttons and elements',
      importance: 'high',
    },
    {
      id: 'on-secondary-on-secondary',
      ratio: contrastRatios.onSecondaryOnSecondary,
      foreground: onColors.onSecondary,
      background: colors.secondary,
      description: 'Text on secondary buttons and elements',
      importance: 'medium',
    },
    {
      id: 'on-success-on-success',
      ratio: contrastRatios.onSuccessOnSuccess,
      foreground: onColors.onSuccess,
      background: colors.success,
      description: 'Text on success notifications and buttons',
      importance: 'medium',
    },
    {
      id: 'on-warning-on-warning',
      ratio: contrastRatios.onWarningOnWarning,
      foreground: onColors.onWarning,
      background: colors.warning,
      description: 'Text on warning notifications and buttons',
      importance: 'medium',
    },
    {
      id: 'on-error-on-error',
      ratio: contrastRatios.onErrorOnError,
      foreground: onColors.onError,
      background: colors.error,
      description: 'Text on error notifications and buttons',
      importance: 'high',
    },
  ];

  // Generate warnings with detailed recommendations
  checks.forEach(check => {
    let severity: AccessibilityWarning['severity'] = 'info';
    let message = '';

    if (check.ratio < 3) {
      severity = 'error';
      message = `Critical: ${check.description} fails WCAG standards (${check.ratio.toFixed(2)}:1)`;
    } else if (check.ratio < 4.5) {
      severity = check.importance === 'critical' ? 'error' : 'warning';
      message = `${check.description} doesn't meet WCAG AA requirements (${check.ratio.toFixed(2)}:1)`;
    } else if (check.ratio < 7) {
      severity = 'info';
      message = `${check.description} meets WCAG AA but not AAA (${check.ratio.toFixed(2)}:1)`;
    } else if (check.importance === 'critical' || check.importance === 'high') {
      // Only add info for excellent contrast if it's important
      severity = 'info';
      message = `Excellent: ${check.description} exceeds WCAG AAA standards (${check.ratio.toFixed(2)}:1)`;
    }

    if (message) {
      warnings.push({
        id: check.id,
        severity,
        message,
        foreground: check.foreground,
        background: check.background,
        contrastRatio: check.ratio,
        recommendation: generateRecommendation(check.ratio, check.description),
      });
    }
  });

  // Additional checks for color differentiation
  const primarySecondaryContrast = getContrastRatio(
    colors.primary,
    colors.secondary
  );
  if (primarySecondaryContrast < 2) {
    warnings.push({
      id: 'primary-secondary-similarity',
      severity: 'warning',
      message: `Primary and secondary colors are too similar (${primarySecondaryContrast.toFixed(2)}:1)`,
      foreground: colors.primary,
      background: colors.secondary,
      contrastRatio: primarySecondaryContrast,
      recommendation:
        'Use more distinct colors for primary and secondary to improve visual hierarchy',
    });
  }

  // Check background/surface distinction
  const backgroundSurfaceContrast = getContrastRatio(
    colors.background,
    colors.surface
  );
  if (backgroundSurfaceContrast < 1.2) {
    warnings.push({
      id: 'background-surface-similarity',
      severity: 'info',
      message: `Background and surface colors are very similar (${backgroundSurfaceContrast.toFixed(2)}:1)`,
      foreground: colors.background,
      background: colors.surface,
      contrastRatio: backgroundSurfaceContrast,
      recommendation:
        'Consider increasing contrast between background and surface for better visual depth',
    });
  }

  // Calculate overall compliance
  const criticalRatios = [
    contrastRatios.textOnBackground,
    contrastRatios.textOnSurface,
    contrastRatios.onPrimaryOnPrimary,
    contrastRatios.onErrorOnError,
  ];

  const wcagAACompliant =
    criticalRatios.every(ratio => ratio >= 4.5) &&
    Object.values(contrastRatios).every(ratio => ratio >= 3);

  const wcagAAACompliant = Object.values(contrastRatios).every(
    ratio => ratio >= 7
  );

  // Calculate sophisticated overall score
  const overallScore = calculateAccessibilityScore(contrastRatios, warnings);

  return {
    contrastRatios,
    warnings,
    wcagAACompliant,
    wcagAAACompliant,
    overallScore,
  };
}

/**
 * Convert HSL to HSV
 */
function hslToHsv(
  h: number,
  sParam: number,
  lParam: number
): { h: number; s: number; v: number } {
  const s = sParam / 100;
  const l = lParam / 100;

  const v = l + s * Math.min(l, 1 - l);
  const sNew = v === 0 ? 0 : 2 * (1 - l / v);

  return {
    h,
    s: sNew * 100,
    v: v * 100,
  };
}

/**
 * Parse color string to ColorPickerValue
 */
export function parseColor(color: string): ColorPickerValue {
  const rgb = hexToRgb(color);
  if (!rgb) throw new Error(`Invalid color: ${color}`);

  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

  // Convert HSL to HSV
  const hsv = hslToHsv(hsl.h, hsl.s, hsl.l);

  return {
    hex: color,
    rgb,
    hsl,
    hsv,
  };
}

/**
 * Check if a color is valid hex
 */
export function isValidHex(color: string): boolean {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
}

/**
 * Lighten a color by a percentage
 */
export function lightenColor(color: string, percentage: number): string {
  const rgb = hexToRgb(color);
  if (!rgb) return color;

  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  hsl.l = Math.min(100, hsl.l + percentage);

  const newRgb = hslToRgb(hsl.h, hsl.s, hsl.l);
  return rgbToHex(newRgb.r, newRgb.g, newRgb.b);
}

/**
 * Darken a color by a percentage
 */
export function darkenColor(color: string, percentage: number): string {
  const rgb = hexToRgb(color);
  if (!rgb) return color;

  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
  hsl.l = Math.max(0, hsl.l - percentage);

  const newRgb = hslToRgb(hsl.h, hsl.s, hsl.l);
  return rgbToHex(newRgb.r, newRgb.g, newRgb.b);
}
